import { useState, useEffect, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList } from 'recharts';
import { Calendar, ChevronUp, ChevronDown, Download, UserX } from 'lucide-react';
import useSWR from 'swr';
import axios from 'axios';
import * as htmlToImage from 'html-to-image';
import { saveAs } from 'file-saver';
import { useParams } from 'react-router-dom';
import { useTranslation } from '../../../../TranslationContext';

// Types
interface User {
  id: string;
  status: string;
  lastSuccessful?: string; // Last successful login date
  lastActivity?: string; // Last activity date
  data: [];
 
}

interface PeriodOption {
  label: string;
  value: number; // in days
}

// Fetcher for SWR
const axiosFetcher = async (url: string) => {
  const response = await axios.get(url, {
    withCredentials:true,
    // headers: {
    //   Authorization: `Bearer ${localStorage.getItem('access_token')}`,
    // },
  });
  return response.data;
};

export default function InactiveUsersChart() {
  const { t } = useTranslation();
  const companyId = localStorage.getItem('company');
  const { appId } = useParams<{ appId: string }>();
  const API_BASE_URL = import.meta.env.VITE_API;
  
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodOption>({ label: t('30_days'), value: 30 });
  const [isPeriodDropdownOpen, setIsPeriodDropdownOpen] = useState(false);
  const chartRef = useRef<HTMLDivElement>(null);
  
  // Period options
  const PERIOD_OPTIONS: PeriodOption[] = [
    { label: t('days30'), value: 30 },
    { label: t('days60'), value: 60 },
    { label: t('days90'), value: 90 },
    { label: t('days180'), value: 180 },
  ];
  
  // Fetch user data
  const { data: usersData } = useSWR<User>(
    companyId && appId ? `${API_BASE_URL}/Company/${companyId}/applications/${appId}/users` : null,
    axiosFetcher
  );

  // Calculate inactive user statistics
  const [inactiveUserStats, setInactiveUserStats] = useState({
    percentage: 0,
    count: 0,
    totalUsers: 0,
    recentlyInactive: 0,
  });
  
  useEffect(() => {
    if (usersData?.data) {
      const now = new Date();
      const inactivityThreshold = new Date();
      inactivityThreshold.setDate(now.getDate() - selectedPeriod.value);
      
      const users = usersData.data;
      const totalUsers = users.length;
      
      if (totalUsers === 0) {
        setInactiveUserStats({
          percentage: 0,
          count: 0,
          totalUsers: 0,
          recentlyInactive: 0,
        });
        return;
      }
      
      const inactiveUsers = users.filter((user: { lastActivity: string | number | Date; lastSuccessful: string | number | Date; }) => {
        const lastActivityDate = user.lastActivity ? new Date(user.lastActivity) : 
                              user.lastSuccessful ? new Date(user.lastSuccessful) : null;
        return !lastActivityDate || lastActivityDate < inactivityThreshold;
      });
      
      const recentlyInactive = inactiveUsers.filter((user: { lastActivity: string | number | Date; lastSuccessful: string | number | Date; }) => {
        const lastActivityDate = user.lastActivity ? new Date(user.lastActivity) : 
                               user.lastSuccessful ? new Date(user.lastSuccessful) : null;
        if (!lastActivityDate) return true;
        
        const becameInactiveDate = new Date(lastActivityDate);
        becameInactiveDate.setDate(lastActivityDate.getDate() + selectedPeriod.value);
        return becameInactiveDate >= now;
      }).length;
      
      const percentage = Math.round((inactiveUsers.length / totalUsers) * 100);
      setInactiveUserStats({
        percentage,
        count: inactiveUsers.length,
        totalUsers,
        recentlyInactive,
      });
    }
  }, [usersData, selectedPeriod]);

  // Chart data
  const chartData = [
    {
      name: t('inactive_users_period', { period: selectedPeriod.label }),
      value: inactiveUserStats.percentage,
      fill: '#8896a7',
    },
    {
      name: t('active_users'),
      value: 100 - inactiveUserStats.percentage,
      fill: '#879bae',
    },
  ];

  // // Download function
  // const handleDownload = async (format: 'png' | 'jpeg' | 'pdf') => {
  //   if (!chartRef.current) return;

  //   try {
  //     const chartElement = chartRef.current;
      
  //     const originalBackground = chartElement.style.backgroundColor;
  //     chartElement.style.backgroundColor = 'white';
  //     chartElement.style.padding = '20px';
      
  //     let dataUrl;
  //     if (format === 'pdf') {
  //       dataUrl = await htmlToImage.toPng(chartElement);
  //       const { jsPDF } = await import('jspdf');
  //       const pdf = new jsPDF('landscape');
  //       pdf.addImage(dataUrl, 'PNG', 15, 15, 260, 150);
  //       pdf.save(`${t('inactive_users_kpi')}-${selectedPeriod.label}.pdf`);
  //     } else {
  //       dataUrl = await htmlToImage[`to${format.toUpperCase()}` as 'toPNG' | 'toJPEG'](chartElement, {
  //         quality: 0.95,
  //         backgroundColor: 'white',
  //       });
  //       saveAs(dataUrl, `${t('inactive_users_kpi')}-${selectedPeriod.label}.${format}`);
  //     }
      
  //     chartElement.style.backgroundColor = originalBackground;
  //     chartElement.style.padding = '';
      
  //   } catch (err) {
  //     console.error('Download error:', err);
  //     alert(t('download_error'));
  //   }
  // };

  // if (isLoading) {
  //   return (
  //     <div className="flex justify-center items-center h-64" aria-label={t('loading')}>
  //       {t('loading')}
  //     </div>
  //   );
  // }
  // if (error) {
  //   return (
  //     <div className="flex justify-center items-center h-64 text-red-500" aria-label={t('error_loading')}>
  //       {t('error_loading')}
  //     </div>
  //   );
  // }
  // Download function corrigée
const handleDownload = async (format: 'png' | 'jpeg' | 'pdf') => {
  if (!chartRef.current) return;

  try {
    const chartElement = chartRef.current;

    const originalBackground = chartElement.style.backgroundColor;
    const originalPadding = chartElement.style.padding;
    chartElement.style.backgroundColor = 'white';
    chartElement.style.padding = '20px';

    let dataUrl: string;

    if (format === 'pdf') {
      dataUrl = await htmlToImage.toPng(chartElement);
      const { jsPDF } = await import('jspdf');
      const pdf = new jsPDF('landscape');
      pdf.addImage(dataUrl, 'PNG', 15, 15, 260, 150);
      pdf.save(`${t('inactive_users_kpi')}-${selectedPeriod.label}.pdf`);
    } else {
      const formatFunctionMap: Record<'png' | 'jpeg', (node: HTMLElement, options?: any) => Promise<string>> = {
        png: htmlToImage.toPng,
        jpeg: htmlToImage.toJpeg,
      };

      const exportFn = formatFunctionMap[format];
      dataUrl = await exportFn(chartElement, {
        quality: 0.95,
        backgroundColor: 'white',
      });

      saveAs(dataUrl, `${t('inactive_users_kpi')}-${selectedPeriod.label}.${format}`);
    }

    chartElement.style.backgroundColor = originalBackground;
    chartElement.style.padding = originalPadding;

  } catch (err) {
    console.error('Download error:', err);
    alert(t('download_error'));
  }
};


  return (
    <div className="bg-white shadow rounded-lg p-6" ref={chartRef}>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-medium text-gray-900 flex items-center">
          <UserX className="mr-2 text-gray-500" size={20} />
          {t('user_inactivity_status')}
        </h2>
        
        <div className="flex space-x-2">
          <div className="relative">
            <button
              onClick={() => setIsPeriodDropdownOpen(!isPeriodDropdownOpen)}
              className="flex items-center space-x-2 px-3 py-2 text-sm border rounded-md hover:bg-gray-50"
              aria-label={t('select_period')}
            >
              <Calendar size={16} />
              <span>{selectedPeriod.label}</span>
              {isPeriodDropdownOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            
            {isPeriodDropdownOpen && (
              <div className="absolute right-0 mt-1 w-32 bg-white border rounded-md shadow-lg z-10">
                {PERIOD_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      setSelectedPeriod(option);
                      setIsPeriodDropdownOpen(false);
                    }}
                    className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                      selectedPeriod.value === option.value ? 'bg-gray-100 font-medium' : ''
                    }`}
                    aria-label={t('select_option', { label: option.label })}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          <div className="relative">
            <button
              onClick={() => document.getElementById('download-dropdown-inactive')?.classList.toggle('hidden')}
              className="flex items-center space-x-2 px-3 py-2 text-sm border rounded-md hover:bg-gray-50"
              aria-label={t('download')}
            >
              <Download size={16} />
              <span>{t('download')}</span>
            </button>
            
            <div id="download-dropdown-inactive" className="hidden absolute right-0 mt-1 w-32 bg-white border rounded-md shadow-lg z-10">
              <button
                onClick={() => handleDownload('png')}
                className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                aria-label={t('download_png')}
              >
                {t('png')}
              </button>
              <button
                onClick={() => handleDownload('jpeg')}
                className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                aria-label={t('download_jpeg')}
              >
                {t('jpeg')}
              </button>
              <button
                onClick={() => handleDownload('pdf')}
                className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                aria-label={t('download_pdf')}
              >
                {t('pdf')}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis 
              type="number" 
              domain={[0, 100]} 
              tick={{ fill: '#4a4a4a' }}
              axisLine={{ stroke: '#4a4a4a' }}
            />
            <YAxis 
              dataKey="name" 
              type="category" 
              width={120}
              tick={{ fill: '#4a4a4a' }}
              axisLine={{ stroke: '#4a4a4a' }}
            />
            <Tooltip 
              formatter={(value) => [`${value}%`, t('percentage')]}
              contentStyle={{ backgroundColor: '#ffffff', borderColor: '#8896a7' }}
            />
            <Bar dataKey="value" barSize={30}>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
              <LabelList 
                dataKey="value" 
                position="right" 
                formatter={(value: any) => `${value}%`}
                fill="#3d3d3d"
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      
      <div className="mt-4 text-sm text-gray-500">
        <p>
          {t('inactive_user_summary', {
            percentage: inactiveUserStats.percentage,
            count: inactiveUserStats.count,
            totalUsers: inactiveUserStats.totalUsers,
            period: selectedPeriod.label,
          })}
        </p>
        <p className="mt-1">
          {t('recently_inactive_summary', {
            count: inactiveUserStats.recentlyInactive,
          })}
        </p>
        {inactiveUserStats.percentage > 40 && (
          <p className="mt-2 text-yellow-600 flex items-center">
            <UserX className="mr-1" size={14} />
            {t('high_inactivity_warning')}
          </p>
        )}
      </div>
    </div>
  );
}
